/* =============================================================
   useSemesters.js — CRUD for semesters (exchange terms)
   -------------------------------------------------------------
   A semester is a neutral container (e.g. "Fall 2025").
   Inside each semester, multiple process templates can exist
   (one per student type/category combination).

   Returns:
     semesters       — open semesters only (is_closed = false), newest first
     loading / saving / error
     createSemester  — creates a new semester with a free-text name
     updateSemester  — renames a semester
     deleteSemester  — deletes a semester (students keep semester_id = null)
     closeSemester   — archives all students + sets is_closed = true
                       (semester row stays in DB for historical reference)
   ============================================================= */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth }  from '../context/AuthContext';

// Broadcast to all useSemesters instances when data changes
const SEMESTERS_EVENT = 'unixplore:semesters-updated';
const notifyAll = () => window.dispatchEvent(new CustomEvent(SEMESTERS_EVENT));

export function useSemesters() {
  const { user } = useAuth();

  const [semesters, setSemesters] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState(null);


  /* ── fetch ─────────────────────────────────────────────────── */
  // Only returns OPEN semesters (is_closed = false).
  // Closed semesters are hidden from Settings, AddStudent, etc.
  // but remain in the DB so archived students keep their semester_id.
  const fetchSemesters = useCallback(async () => {
    if (!user?.universityId) return;
    setLoading(true);
    const { data, error: e } = await supabase
      .from('semesters')
      .select('id, name, created_at, is_closed')
      .eq('university_id', user.universityId)
      .eq('is_closed', false)
      .order('created_at', { ascending: false });
    if (e) setError(e.message);
    else   setSemesters(data ?? []);
    setLoading(false);
  }, [user?.universityId]);

  useEffect(() => { fetchSemesters(); }, [fetchSemesters]);

  // Re-fetch when any other instance writes (delete/create/update/close/restore)
  useEffect(() => {
    window.addEventListener(SEMESTERS_EVENT, fetchSemesters);
    return () => window.removeEventListener(SEMESTERS_EVENT, fetchSemesters);
  }, [fetchSemesters]);


  /* ── createSemester ─────────────────────────────────────────── */
  const createSemester = async (name) => {
    if (!user?.universityId || !name?.trim()) return null;
    setSaving(true);
    setError(null);
    const { data, error: e } = await supabase
      .from('semesters')
      .insert({ university_id: user.universityId, name: name.trim(), is_closed: false })
      .select('id')
      .single();
    if (e) { setError(e.message); setSaving(false); return null; }
    notifyAll();
    setSaving(false);
    return data.id;
  };


  /* ── updateSemester ─────────────────────────────────────────── */
  const updateSemester = async (id, name) => {
    if (!name?.trim()) return;
    setSaving(true);
    setError(null);
    const { error: e } = await supabase
      .from('semesters')
      .update({ name: name.trim() })
      .eq('id', id);
    if (e) setError(e.message);
    else   notifyAll();
    setSaving(false);
  };


  /* ── deleteSemester ─────────────────────────────────────────── */
  const deleteSemester = async (id) => {
    setSaving(true);
    setError(null);
    const { error: e } = await supabase
      .from('semesters')
      .delete()
      .eq('id', id);
    if (e) setError(e.message);
    else   notifyAll();
    setSaving(false);
  };


  /* ── closeSemester ───────────────────────────────────────────── */
  // 1. Archives all active students in this term (archived = true)
  // 2. Marks the semester as closed (is_closed = true)
  //    → disappears from Settings / AddStudent / ProcessConfig
  //    → semester row stays in DB so students keep their semester_id
  //    → restore in Archive tab brings everything back exactly as before
  const closeSemester = async (id) => {
    setSaving(true);
    setError(null);

    // Archive all non-archived students in this term
    const { error: archiveErr } = await supabase
      .from('students')
      .update({ archived: true })
      .eq('semester_id', id)
      .eq('archived', false);

    if (archiveErr) { setError(archiveErr.message); setSaving(false); return; }

    // Mark semester as closed — hides it from all active lists
    const { error: closeErr } = await supabase
      .from('semesters')
      .update({ is_closed: true })
      .eq('id', id);

    if (closeErr) setError(closeErr.message);
    else          notifyAll();

    setSaving(false);
  };


  return { semesters, loading, saving, error, createSemester, updateSemester, deleteSemester, closeSemester };
}
